// ----------      START   IMPORT  BLOCK        ----------
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, GeoAlt } from 'react-bootstrap-icons';
import { useLanguage } from '../../../API/Context/LanguageContext';
import { MEXICAN_STATES, type MexicanState } from '../../../ConfigFiles/staticData/mexicanStates';
import { MEDICAL_SPECIALTIES, type MedicalSpecialty } from '../../../ConfigFiles/staticData/medicalSpecialties';
import VocabLandingPage from '../../../ConfigFiles/multilingual/VocabLandingPage';
import { VocabButtons as btn_vocab } from '../../../ConfigFiles/multilingual/VocabButtons';
import { SearchResultsList } from './SearchResultsList';
import './LandingPageSearchBar.css';
// ----------      END   IMPORT  BLOCK        ----------

export const LandingPageSearchBar: React.FC = () => {
  const { lan } = useLanguage();
  const navigate = useNavigate();

  const langIdx = lan === 1 ? 1 : 0;

  // Query state
  const [queryText, setQueryText] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<MedicalSpecialty | null>(null);
  const [showQueryDropdown, setShowQueryDropdown] = useState(false);

  // Location state
  const [locationText, setLocationText] = useState('');
  const [selectedState, setSelectedState] = useState<MexicanState | null>(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowQueryDropdown(false);
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered Specialties
  const filteredSpecialties = MEDICAL_SPECIALTIES.filter((spec) => {
    const searchLower = queryText.toLowerCase().trim();
    if (!searchLower) return true;

    const specialistName = spec.specialist_name[langIdx]?.toLowerCase() || '';
    const specName = spec.specialty_name[langIdx]?.toLowerCase() || '';
    return specialistName.includes(searchLower) || specName.includes(searchLower);
  });

  // Filtered States
  const filteredStates = MEXICAN_STATES.filter((st) => {
    const searchLower = locationText.toLowerCase().trim();
    if (!searchLower) return true;
    return st.name.toLowerCase().includes(searchLower);
  });

  const handleSelectSpecialty = (spec: MedicalSpecialty) => {
    setSelectedSpecialty(spec);
    setQueryText(spec.specialist_name[langIdx] || spec.specialty_name[langIdx]);
    setShowQueryDropdown(false);
  };

  const handleSelectState = (st: MexicanState) => {
    setSelectedState(st);
    setLocationText(st.name);
    setShowLocationDropdown(false);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (selectedSpecialty) {
      params.set('specialty_id', selectedSpecialty.id);
      params.set('category', String(selectedSpecialty.category));
    } else if (queryText.trim()) {
      params.set('query', queryText.trim());
    }

    if (selectedState) {
      params.set('state_id', selectedState.id);
      params.set('state_name', selectedState.name);
    } else if (locationText.trim()) {
      params.set('location', locationText.trim());
    }

    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="search-bar-container" ref={containerRef}>
      {/* --- Specialty / Specialist Input --- */}
      <div className="search-field-wrapper">
        <div className="field-icon">
          <Search size={18} />
        </div>
        <input
          type="text"
          className="search-input"
          placeholder={VocabLandingPage.searchbar_placeholder?.[lan] || "Specialist, specialty, or condition..."}
          value={queryText}
          onFocus={() => {
            setShowQueryDropdown(true);
            setShowLocationDropdown(false);
          }}
          onChange={(e) => {
            setQueryText(e.target.value);
            setSelectedSpecialty(null);
            setShowQueryDropdown(true);
          }}
        />

        {showQueryDropdown && (
          <SearchResultsList
            items={filteredSpecialties}
            itemType="specialty"
            langIdx={langIdx}
            queryText={queryText}
            onSelect={handleSelectSpecialty}
          />
        )}
      </div>

      <div className="search-divider" />

      {/* --- Location Input --- */}
      <div className="search-field-wrapper">
        <div className="field-icon">
          <GeoAlt size={18} />
        </div>
        <input
          type="text"
          className="search-input"
          placeholder={VocabLandingPage.select_city_placeholder?.[lan] || "Estado o ciudad..."}
          value={locationText}
          onFocus={() => {
            setShowLocationDropdown(true);
            setShowQueryDropdown(false);
          }}
          onChange={(e) => {
            setLocationText(e.target.value);
            setSelectedState(null);
            setShowLocationDropdown(true);
          }}
        />

        {showLocationDropdown && (
          <SearchResultsList
            items={filteredStates}
            itemType="state"
            langIdx={langIdx}
            queryText={locationText}
            onSelect={handleSelectState}
          />
        )}
      </div>

      {/* --- Submit Action --- */}
      <button type="button" className="search-submit-btn" onClick={handleSearch}>
        {btn_vocab.search?.[lan] || "Buscar"}
      </button>
    </div>
  );
};